import { useTranslation } from "react-i18next";
import {
  DESKTOP_TRIGGER_KEYS,
  type DesktopPermissionStatus,
  type DesktopTriggerKey,
} from "@/clients/desktop.client";
import {
  CheckIcon,
  ChevronRightIcon,
  CursorIcon,
  MicrophoneIcon,
  SystemIcon,
} from "@/components/Icons";
import {
  desktopTriggerKeycap,
  desktopTriggerLabel,
} from "@/desktop-trigger-label";
import type { DesktopHostModel } from "@/models/useDesktopHost";
import { APP_DOWNLOAD_URLS } from "@/product-links";

const permissionLabel = (
  status: DesktopPermissionStatus["microphone"] | "granted" | "denied",
): string => {
  if (status === "granted") return "Allowed";
  if (status === "denied" || status === "restricted") {
    return "Needs attention";
  }
  if (status === "not-determined") return "Not allowed yet";
  return "Checking…";
};

const rowClass =
  "flex min-h-[76px] w-full items-center gap-4 px-5 py-4 text-start transition-[background-color,transform] duration-200 hover:bg-[var(--glass-bg-hover)] active:scale-[0.995] disabled:opacity-50";

const DesktopDictationSettings = ({
  host,
  mode = "dictation",
}: {
  host: DesktopHostModel;
  mode?: "dictation" | "app";
}) => {
  const { t } = useTranslation();
  const {
    info,
    settings,
    permissions,
    update,
    busy,
    operationFailed,
    updateSettings,
    requestPermission,
    checkForUpdates,
    installUpdate,
  } = host;
  const unavailable = !info;
  const microphoneGranted = permissions?.microphone === "granted";
  const accessibilityGranted = permissions?.accessibility === "granted";

  if (mode === "app") {
    return (
      <div
        data-testid="desktop-app-settings-panel"
        className="glass-tile overflow-hidden rounded-[26px] text-[var(--text-primary)]"
      >
        {update.state === "blocked" ? (
          <button
            type="button"
            data-action-id="settings.desktop.downloadUpdate"
            disabled={unavailable}
            onClick={() => {
              if (!info) return;
              window.open(
                APP_DOWNLOAD_URLS[
                  info.platform === "windows" ? "windows" : "mac"
                ],
                "_blank",
              );
            }}
            className={rowClass}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
              <SystemIcon size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold tracking-tight">
                {t("desktopDictation.updateBlockedTitle", {
                  defaultValue: "Update could not install",
                })}
              </span>
              <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                {t("desktopDictation.updateBlocked", {
                  defaultValue:
                    "Download the latest version to keep getting updates",
                })}
              </span>
            </span>
            <ChevronRightIcon
              size={20}
              className="text-[var(--text-tertiary)]"
            />
          </button>
        ) : update.state === "downloaded" ? (
          <button
            type="button"
            data-action-id="settings.desktop.installUpdate"
            disabled={unavailable}
            onClick={() => void installUpdate()}
            className={rowClass}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
              <SystemIcon size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold tracking-tight">
                {t("desktopDictation.installUpdateTitle", {
                  defaultValue: "Desktop update ready",
                })}
              </span>
              <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                {t("desktopDictation.installUpdate", {
                  defaultValue: "Restart to install {{version}}",
                  version: update.version ?? "update",
                })}
              </span>
            </span>
            <ChevronRightIcon
              size={20}
              className="text-[var(--text-tertiary)]"
            />
          </button>
        ) : (
          <button
            type="button"
            data-action-id="settings.desktop.checkUpdate"
            disabled={unavailable || update.state === "checking"}
            onClick={() => void checkForUpdates()}
            className={rowClass}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
              <SystemIcon size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold tracking-tight">
                {update.state === "checking"
                  ? t("desktopDictation.checkingUpdate", {
                      defaultValue: "Checking for updates…",
                    })
                  : t("desktopDictation.checkUpdate", {
                      defaultValue: "Check for desktop update",
                    })}
              </span>
              <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                {t("desktopDictation.currentVersion", {
                  defaultValue: "Version {{version}}",
                  version: info?.appVersion ?? "…",
                })}
              </span>
            </span>
            <ChevronRightIcon
              size={20}
              className="text-[var(--text-tertiary)]"
            />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="desktop-settings-panel"
      className="space-y-7 text-[var(--text-primary)]"
    >
      {operationFailed ? (
        <p
          role="status"
          className="rounded-[22px] bg-[var(--destructive-bg)] px-4 py-3 text-[13px] font-medium text-[var(--destructive)]"
        >
          {t("desktopDictation.operationFailed", {
            defaultValue:
              "That desktop setting could not be updated. Try again.",
          })}
        </p>
      ) : null}

      <section className="space-y-3">
        <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
          {t("desktopDictation.desktopControls", {
            defaultValue: "Desktop controls",
          })}
        </p>
        <div className="glass-tile overflow-hidden rounded-[26px]">
          {settings ? (
            <>
              <label className={rowClass}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)] text-[var(--text-primary)]">
                  <kbd className="max-w-9 truncate rounded-md bg-[var(--bg-secondary)] px-1.5 py-1 font-mono text-[10px] font-semibold shadow-sm">
                    {desktopTriggerKeycap(settings.triggerKey, info?.platform)}
                  </kbd>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[16px] font-semibold tracking-tight">
                    {t("desktopDictation.trigger", {
                      defaultValue: "Global shortcut",
                    })}
                  </span>
                  <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                    {t("desktopDictation.triggerSubtitle", {
                      defaultValue: "Starts and stops dictation in any app",
                    })}
                  </span>
                </span>
                <select
                  data-action-id="settings.desktop.trigger"
                  aria-label={t("desktopDictation.trigger", {
                    defaultValue: "Global shortcut",
                  })}
                  value={settings.triggerKey}
                  disabled={busy || unavailable}
                  onChange={(event) =>
                    void updateSettings({
                      triggerKey: event.target.value as DesktopTriggerKey,
                    })
                  }
                  className="max-w-[128px] rounded-xl bg-[var(--fill-1)] px-3 py-2 text-[13px] font-semibold text-[var(--text-primary)] outline-none focus:ring-2 focus:ring-[var(--accent)] disabled:opacity-50"
                >
                  {DESKTOP_TRIGGER_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {desktopTriggerLabel(key, info?.platform, t)}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                role="switch"
                aria-checked={settings.showIndicator}
                data-action-id="settings.desktop.indicator"
                disabled={busy || unavailable}
                onClick={() =>
                  void updateSettings({
                    showIndicator: !settings.showIndicator,
                  })
                }
                className={rowClass}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
                  <SystemIcon size={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[16px] font-semibold tracking-tight">
                    {t("desktopDictation.showIndicator", {
                      defaultValue: "Show dictation status",
                    })}
                  </span>
                  <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                    {t("desktopDictation.showIndicatorSubtitle", {
                      defaultValue:
                        "Keep a small status pill visible while you speak",
                    })}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                    settings.showIndicator
                      ? "bg-[var(--accent)]"
                      : "bg-[var(--fill-3)]"
                  }`}
                >
                  <span
                    className={`switch-thumb absolute left-1 top-1 h-5 w-5 rounded-full shadow-sm transition-transform ${
                      settings.showIndicator ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </span>
              </button>

              <button
                type="button"
                role="switch"
                aria-checked={settings.launchAtLogin}
                data-action-id="settings.desktop.launchAtLogin"
                disabled={busy || unavailable}
                onClick={() =>
                  void updateSettings({
                    launchAtLogin: !settings.launchAtLogin,
                  })
                }
                className={rowClass}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
                  <SystemIcon size={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[16px] font-semibold tracking-tight">
                    {t("desktopDictation.launchAtLogin", {
                      defaultValue: "Open at login",
                    })}
                  </span>
                  <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                    {t("desktopDictation.launchAtLoginSubtitle", {
                      defaultValue:
                        "Keep your shortcut ready after your computer starts",
                    })}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                    settings.launchAtLogin
                      ? "bg-[var(--accent)]"
                      : "bg-[var(--fill-3)]"
                  }`}
                >
                  <span
                    className={`switch-thumb absolute left-1 top-1 h-5 w-5 rounded-full shadow-sm transition-transform ${
                      settings.launchAtLogin ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </span>
              </button>
            </>
          ) : null}
        </div>
      </section>

      <section className="space-y-3">
        <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">
          {t("desktopDictation.accessSection", { defaultValue: "Access" })}
        </p>
        <div className="glass-tile overflow-hidden rounded-[26px]">
          <button
            type="button"
            data-action-id="settings.desktop.microphonePermission"
            disabled={busy || unavailable}
            onClick={() => void requestPermission("microphone")}
            className={rowClass}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
              <MicrophoneIcon size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold tracking-tight">
                {t("desktopDictation.microphone", {
                  defaultValue: "Microphone",
                })}
              </span>
              <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                {permissionLabel(permissions?.microphone ?? "unknown")}
              </span>
            </span>
            {microphoneGranted ? (
              <CheckIcon
                size={18}
                strokeWidth={2.5}
                className="text-[var(--accent)]"
              />
            ) : (
              <ChevronRightIcon
                size={20}
                className="text-[var(--text-tertiary)]"
              />
            )}
          </button>

          <button
            type="button"
            data-action-id="settings.desktop.accessibilityPermission"
            disabled={busy || unavailable}
            onClick={() => void requestPermission("accessibility")}
            className={rowClass}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fill-1)]">
              <CursorIcon size={21} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-semibold tracking-tight">
                {t("desktopDictation.accessibility", {
                  defaultValue: "Text insertion",
                })}
              </span>
              <span className="mt-0.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                {permissionLabel(permissions?.accessibility ?? "unknown")}
              </span>
            </span>
            {accessibilityGranted ? (
              <CheckIcon
                size={18}
                strokeWidth={2.5}
                className="text-[var(--accent)]"
              />
            ) : (
              <ChevronRightIcon
                size={20}
                className="text-[var(--text-tertiary)]"
              />
            )}
          </button>
        </div>
      </section>
    </div>
  );
};

export default DesktopDictationSettings;
